import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ClipboardList, Loader2, AlertCircle, ChevronLeft, ChevronRight, CheckCircle, XCircle, HelpCircle, ChevronDown, MapPin, Filter } from 'lucide-react';
import {
  useGetQuestionSummaryQuery,
  type QuestionSummaryItem,
} from '../../redux/api/qaApi';
import { useAppSelector } from '../../redux/hooks';

interface AnswerDetail {
  pageNumber: number;
  answerType: string; // 'pass' | 'fail' | 'ca'
  description: string | null;
  boundingBox: Record<string, number> | null;
}

// Map: questionId -> AnswerDetail[]
type AnswerDetailMap = Record<string, AnswerDetail[]>;

const statusConfig = {
  pass: { label: 'Pass', color: 'text-green-400', bg: 'bg-green-400/15', border: 'border-green-400/30', Icon: CheckCircle },
  fail: { label: 'Fail', color: 'text-red-400', bg: 'bg-red-400/15', border: 'border-red-400/30', Icon: XCircle },
  ca: { label: 'CA', color: 'text-yellow-400', bg: 'bg-yellow-400/15', border: 'border-yellow-400/30', Icon: HelpCircle },
} as const;

function getStatusKey(answerType: string): 'pass' | 'fail' | 'ca' {
  if (answerType === 'pass') return 'pass';
  if (answerType === 'fail') return 'fail';
  return 'ca';
}

function navigateToPage(pageNumber: number, boundingBox?: Record<string, number> | null) {
  window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
    detail: {
      page: pageNumber,
      boundingBox: boundingBox && boundingBox.x_min != null ? boundingBox : undefined,
    },
  }));
}

/** Extract answers from the summary data (includes descriptions and bounding boxes) */
function getAnswersFromSummary(item: QuestionSummaryItem): AnswerDetail[] {
  const answers: AnswerDetail[] = [];

  const passDetails = item.pass_page_details?.length
    ? item.pass_page_details
    : item.pages_with_passes.map((p) => ({ page_number: p, bounding_box: null }));
  passDetails.forEach((d) => answers.push({ pageNumber: d.page_number, answerType: 'pass', description: ('description' in d ? d.description : null) ?? null, boundingBox: d.bounding_box as any }));

  const failDetails = item.fail_page_details?.length
    ? item.fail_page_details
    : item.pages_with_failures.map((p) => ({ page_number: p, bounding_box: null }));
  failDetails.forEach((d) => answers.push({ pageNumber: d.page_number, answerType: 'fail', description: ('description' in d ? d.description : null) ?? null, boundingBox: d.bounding_box as any }));

  const caDetails = item.ca_page_details?.length
    ? item.ca_page_details
    : item.pages_with_ca.map((p) => ({ page_number: p, bounding_box: null }));
  caDetails.forEach((d) => answers.push({ pageNumber: d.page_number, answerType: 'ca', description: ('description' in d ? d.description : null) ?? null, boundingBox: d.bounding_box as any }));

  answers.sort((a, b) => a.pageNumber - b.pageNumber);
  return answers;
}

const QuestionSummaryTab: React.FC = () => {
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 100;
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [answerDetailMap, setAnswerDetailMap] = useState<AnswerDetailMap>({});
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showFailuresOnly, setShowFailuresOnly] = useState(false);

  const queryParams = useMemo(() => {
    if (!siteId) return undefined;
    return { site_id: siteId, limit: 500, offset: 0 };
  }, [siteId]);

  const { data: summaryData, isLoading: isLoadingSummary, error: summaryError } = useGetQuestionSummaryQuery(
    queryParams,
    { skip: !siteId }
  );

  // Reset on site change
  useEffect(() => {
    setCurrentPage(1);
    setExpandedIds(new Set());
    setSelectedCategory('all');
    setShowFailuresOnly(false);
  }, [siteId]);

  const isLoading = isLoadingSummary;
  const error = summaryError;
  const allItems = summaryData?.items || [];

  // Extract unique categories for filter dropdown
  const categories = useMemo(() => {
    const cats = new Set<string>();
    allItems.forEach(item => { if (item.category) cats.add(item.category); });
    return Array.from(cats).sort();
  }, [allItems]);

  // Apply filters
  const filteredItems = useMemo(() => {
    let items = allItems;
    if (selectedCategory !== 'all') {
      items = items.filter(item => item.category === selectedCategory);
    }
    if (showFailuresOnly) {
      items = items.filter(item => item.total_failures > 0);
    }
    return items;
  }, [allItems, selectedCategory, showFailuresOnly]);

  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;
  const startItem = totalItems > 0 ? offset + 1 : 0;
  const endItem = Math.min(offset + itemsPerPage, totalItems);
  const paginatedData = filteredItems.slice(offset, offset + itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, showFailuresOnly]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const toggleExpanded = useCallback((questionId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(questionId)) {
        next.delete(questionId);
      } else {
        next.add(questionId);
      }
      return next;
    });
  }, []);

  const getAnswers = useCallback((item: QuestionSummaryItem): AnswerDetail[] => {
    return getAnswersFromSummary(item);
  }, []);

  const totalWithResponses = summaryData?.total_with_responses || 0;
  const questionsWithFailures = summaryData?.questions_with_failures || 0;
  const questionsAllPass = summaryData?.questions_all_pass || 0;

  return (
    <div className="eg-page-shell">
      {/* Header */}
      <div className="eg-toolbar-header p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-semibold eg-text-primary mb-1">Answers Summary</h2>
            <p className="text-sm eg-text-secondary">
              {totalWithResponses} questions with responses | {questionsWithFailures} with failures | {questionsAllPass} all pass
            </p>
          </div>
        </div>
        {allItems.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-3.5 h-3.5 eg-text-secondary" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="eg-select px-2.5 py-1.5 text-xs rounded-lg"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowFailuresOnly(prev => !prev)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                showFailuresOnly
                  ? 'bg-red-500/20 text-red-400 border-red-500/30'
                  : 'eg-text-secondary'
              }`}
              style={!showFailuresOnly ? { background: 'var(--color-input-bg)', borderColor: 'var(--color-input-border)' } : undefined}
            >
              <XCircle className="w-3.5 h-3.5" />
              Failures Only
            </button>
            {(selectedCategory !== 'all' || showFailuresOnly) && (
              <span className="text-xs eg-text-secondary">
                {totalItems} result{totalItems !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="eg-page-content">
        {!siteId ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 eg-text-secondary mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">No Site Selected</p>
              <p className="text-sm eg-text-secondary mt-1">
                Please select a site from the sidebar to view answers summary
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-engramiq-green" />
            <span className="ml-3 eg-text-primary">Loading answers summary...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 text-accent-red mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">Error loading answers summary</p>
              <p className="text-sm eg-text-secondary mt-1">
                {error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}
              </p>
            </div>
          </div>
        ) : !summaryData || !summaryData.items || summaryData.items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-16 h-16 eg-text-secondary mb-4" />
            <p className="text-lg eg-text-primary mb-2">No Answer Data Found</p>
            <p className="text-sm eg-text-secondary">
              No QA responses found for the selected site. Make sure QA results have been imported for this site.
            </p>
          </div>
        ) : !paginatedData || paginatedData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="w-16 h-16 eg-text-secondary mb-4" />
            <p className="text-lg eg-text-primary mb-2">No Answers to Display</p>
            <p className="text-sm eg-text-secondary">
              Questions found but no matching answer details available
            </p>
          </div>
        ) : (
          <div className="p-4">
            <div className="eg-card-elevated overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b" style={{ borderColor: 'var(--color-overlay-light)' }}>
                    <tr>
                      <th className="w-8 px-2 py-3" />
                      <th className="px-4 py-3 text-right">
                        <span className="text-sm font-semibold eg-text-primary">ID</span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="text-sm font-semibold eg-text-primary">Check Description</span>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <span className="text-sm font-semibold eg-text-primary">Category</span>
                      </th>
                      <th className="px-4 py-3 text-left whitespace-nowrap">
                        <span className="text-sm font-semibold eg-text-primary">Status</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y" style={{ borderColor: 'var(--color-overlay-light)' }}>
                    {paginatedData.map((item) => {
                      const isExpanded = expandedIds.has(item.question_id);
                      const answers = getAnswers(item);
                      const hasAnswers = answers.length > 0;

                      return (
                        <React.Fragment key={item.question_id}>
                          <tr
                            className={`transition-colors ${hasAnswers ? 'cursor-pointer hover:brightness-95' : ''}`}
                            onClick={() => hasAnswers && toggleExpanded(item.question_id)}
                          >
                            <td className="px-2 py-3 text-center">
                              {hasAnswers && (
                                <ChevronDown
                                  className={`w-4 h-4 eg-text-secondary transition-transform duration-200 ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
                                />
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm eg-text-secondary font-mono text-right">
                              {item.question_id}
                            </td>
                            <td className="px-4 py-3 text-sm eg-text-primary">
                              {item.check_description}
                            </td>
                            <td className="px-4 py-3">
                              <span className="px-2 py-1 bg-blue-400/20 text-blue-400 rounded text-xs">
                                {item.category}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {item.total_passes > 0 && (
                                  <div className="flex items-center gap-1">
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span className="text-xs text-green-400">{item.total_passes}</span>
                                  </div>
                                )}
                                {item.total_failures > 0 && (
                                  <div className="flex items-center gap-1">
                                    <XCircle className="w-4 h-4 text-red-400" />
                                    <span className="text-xs text-red-400">{item.total_failures}</span>
                                  </div>
                                )}
                                {item.total_ca > 0 && (
                                  <div className="flex items-center gap-1">
                                    <HelpCircle className="w-4 h-4 text-yellow-400" />
                                    <span className="text-xs text-yellow-400">{item.total_ca}</span>
                                  </div>
                                )}
                                {item.total_passes === 0 && item.total_failures === 0 && item.total_ca === 0 && (
                                  <span className="text-xs eg-text-secondary">No responses</span>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={5} className="p-0">
                                <div className="border-t" style={{ background: 'var(--color-zone-recessed)', borderColor: 'var(--color-overlay-light)' }}>
                                  <div className="px-6 py-3">
                                    <div className="grid gap-2">
                                      {answers.map((answer, idx) => {
                                        const statusKey = getStatusKey(answer.answerType);
                                        const cfg = statusConfig[statusKey];
                                        const hasLocation = answer.boundingBox && answer.boundingBox.x_min != null;
                                        return (
                                          <div
                                            key={`${answer.answerType}-${answer.pageNumber}-${idx}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => { e.stopPropagation(); navigateToPage(answer.pageNumber, answer.boundingBox); }}
                                            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
                                            className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors hover:brightness-110 ${cfg.bg} ${cfg.border}`}
                                          >
                                            <cfg.Icon className={`w-4 h-4 ${cfg.color} flex-shrink-0`} />
                                            <span className={`text-xs font-medium ${cfg.color} w-8 flex-shrink-0`}>{cfg.label}</span>
                                            <span className="text-xs eg-text-primary flex-shrink-0">
                                              Page {answer.pageNumber}
                                            </span>
                                            {answer.description && (
                                              <span className="text-xs eg-text-secondary flex-1 min-w-0" title={answer.description}>
                                                {answer.description}
                                              </span>
                                            )}
                                            {hasLocation && (
                                              <span className="ml-auto flex items-center gap-1 text-xs text-engramiq-green flex-shrink-0">
                                                <MapPin className="w-3 h-3" />
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm eg-text-secondary">
                  Showing {startItem} to {endItem} of {totalItems} questions
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPage}
                    disabled={currentPage <= 1 || isLoading}
                    className="eg-btn-ghost px-3 py-1.5 rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Previous</span>
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={isLoading}
                          className={`px-3 py-1.5 rounded-lg transition-all ${
                            currentPage === pageNum
                              ? 'bg-engramiq-green text-engramiq-dark-blue font-medium'
                              : 'eg-btn-ghost eg-text-primary'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={currentPage >= totalPages || isLoading}
                    className="eg-btn-ghost px-3 py-1.5 rounded-lg text-engramiq-green transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <span>Next</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionSummaryTab;
